import React, { useRef, useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const SeveritySlider = ({ severity, onChange }) => {
  const sliderRef = useRef(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const severityLevels = [
    { value: 1, label: 'Very Mild', icon: 'Smile', color: 'text-success' },
    { value: 2, label: 'Mild', icon: 'SmilePlus', color: 'text-success' },
    { value: 3, label: 'Moderate', icon: 'Meh', color: 'text-warning' },
    { value: 4, label: 'Severe', icon: 'Frown', color: 'text-error' },
    { value: 5, label: 'Very Severe', icon: 'Angry', color: 'text-error' }
  ];

  const currentLevel = severityLevels?.find(level => level?.value === severity) || severityLevels?.[2];

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e?.targetTouches?.[0]?.clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e?.targetTouches?.[0]?.clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsDragging(false);
      return;
    }

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && severity < 5) {
      onChange(severity + 1);
    }
    if (isRightSwipe && severity > 1) {
      onChange(severity - 1);
    }

    setIsDragging(false);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'ArrowLeft' && severity > 1) {
      onChange(severity - 1);
    } else if (e?.key === 'ArrowRight' && severity < 5) {
      onChange(severity + 1);
    }
  };

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg md:text-xl lg:text-2xl font-heading font-bold text-foreground mb-2">
          How severe is it?
        </h3>
        <p className="text-sm md:text-base text-muted-foreground">
          Move the slider or swipe left/right to adjust severity
        </p>
      </div>
      <div 
        className="bg-card rounded-xl p-6 md:p-8 border border-border"
        ref={sliderRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex flex-col items-center mb-6">
          <div className={`flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-muted transition-transform duration-200 ${isDragging ? 'scale-110' : ''}`}>
            <Icon 
              name={currentLevel?.icon} 
              size={40} 
              className={currentLevel?.color}
              strokeWidth={2}
            />
          </div>
          <span className="text-xl md:text-2xl font-heading font-bold text-foreground">
            {currentLevel?.label}
          </span>
          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
            <Icon name="ArrowLeft" size={14} />
            <span>Swipe to adjust</span>
            <Icon name="ArrowRight" size={14} />
          </div>
        </div>

        <div className="relative px-2">
          <input
            type="range"
            min="1"
            max="5"
            value={severity}
            onChange={(e) => onChange(parseInt(e?.target?.value))}
            onKeyDown={handleKeyDown}
            className="w-full h-3 bg-muted rounded-lg appearance-none cursor-pointer accent-primary focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
            aria-label="Severity level slider"
            aria-valuemin="1"
            aria-valuemax="5"
            aria-valuenow={severity}
            aria-valuetext={currentLevel?.label}
          />
          <div className="flex justify-between mt-4 px-1">
            {severityLevels?.map((level) => (
              <div key={level?.value} className="flex flex-col items-center">
                <Icon 
                  name={level?.icon} 
                  size={20} 
                  className={severity === level?.value ? level?.color : 'text-muted-foreground'}
                  strokeWidth={severity === level?.value ? 2.5 : 2}
                />
                <span className="text-xs text-muted-foreground mt-1 hidden md:block">
                  {level?.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeveritySlider;